import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { WireApiService } from '@frontend/shared';
import {
  ActionKind,
  ConditionNode,
  ConditionOperator,
  ConditionalBranch,
  ConversationType,
  LeafConditionNode,
  LogicalOperatorDto,
  MessageTemplate,
  TriggerKind, CompoundConditionNode,
  WorkflowAction
} from '@clientwire/api';

/**
 * Branch type definition
 */
export interface Branch {
  conditionNode?: ConditionNode;
  action: {
    kind: ActionKind;
    templateName?: string;
  }
}

/**
 * Token type used for parsing rule expressions
 */
export interface Token {
  type: 'branch' | 'logic' | 'leaf' | 'path' | 'value' | 'paren' | 'unknown' | 'action' | 'action_param';
  value: string;
}

// Keyword constants with type safety
export const BranchKeyword = {
  IF: 'IF',
  ELSEIF: 'ELSEIF',
  ELSE: 'ELSE'
}

export const LogicalOperator = {
  AND: 'AND',
  OR: 'OR',
  NOT: 'NOT'
} as const;

export const LeafOperator = {
  EXIST: 'EXIST',
  NOT_EXIST: 'NOT_EXIST',
  EQUALS: 'EQUALS',
  CHANGED: 'CHANGED',
  CHANGED_FROM_TO: 'CHANGED_FROM_TO'
};

export const ActionKeyword = {
  DO: 'DO'
};

export const ActionType = {
  SEND_MESSAGE: 'SEND_MESSAGE',
  NOTIFY_PARTICIPANTS: 'NOTIFY_PARTICIPANTS'
};

export const ActionParam = {
  WITH: 'WITH'
};

@Injectable({ providedIn: 'root' })
export class WorkflowDialogService {
  readonly #wireApiService = inject(WireApiService);

  readonly triggerKinds = signal(Object.values(TriggerKind));
  readonly actionKinds = signal(Object.values(ActionKind));
  readonly conditionOperators = signal(Object.values(ConditionOperator));

  readonly messageTemplates: WritableSignal<MessageTemplate[] | []> = signal([]);
  readonly conversationTypes: WritableSignal<ConversationType[] | []> = signal([]);

  readonly jsonPathPrefix = '$.conversation.conversation_data.';

  loadMessageTemplates(): void {
    this.#wireApiService.messageTemplatesApi.listMessageTemplates({
      includes: []
    })
      .then(response => {
        const filteredResponse = response.messageTemplates.filter(it => !it.isSystemTemplate) || [];
        this.messageTemplates.set(filteredResponse);
      })
      .catch(error => {
        console.error('Error loading message templates:', error);
      });
  }

  loadConversationTypes(): void {
    this.#wireApiService.conversationTypesApi.getConversationTypes()
      .then(response => {
        this.conversationTypes.set(response.conversationTypes || []);
      })
      .catch(error => {
        console.error('Error loading conversation types:', error);
      });
  }

  /**
   * Parse a rule expression into an array of Branches and fallback action
   */
  parseExpression(expression: string): { branches: Branch[], elseAction?: { kind: ActionKind, templateName?: string } } {
    if (!expression.trim()) {
      return { branches: [] };
    }

    const branches: Branch[] = [];
    let elseAction: { kind: ActionKind, templateName?: string } | undefined = undefined;
    const tokens = this.tokenize(expression);

    let currentIndex = 0;

    // Process each branch
    while (currentIndex < tokens.length) {
      const token = tokens[currentIndex];

      if (!token) {
        currentIndex++;
        continue;
      }

      // Skip any non-branch tokens at top level
      if (token.type !== 'branch') {
        currentIndex++;
        continue;
      }

      currentIndex++; // Move past the branch keyword

      // ELSE branch doesn't have conditions - map it to the fallback action
      if (token.value.toUpperCase() === 'ELSE') {
        console.log('Found ELSE token at index', currentIndex);

        // Create a proper branch with a temporary action for the ELSE condition
        const elseBranch: Branch = {
          action: { kind: ActionKind.NotifyParticipants } // Default action
        };

        // Look for "DO" token
        let doIndex = currentIndex;
        while (doIndex < tokens.length) {
          if (tokens[doIndex]?.type === 'action' && tokens[doIndex]?.value.toUpperCase() === 'DO') {
            // Found DO, move past it
            doIndex++;
            break;
          }
          doIndex++;
        }

        // Parse action for ELSE statement with proper debugging
        if (doIndex < tokens.length) {
          console.log('Found DO token, parsing ELSE action starting at index', doIndex);

          // Debug the tokens we're about to parse
          const actionToken = tokens[doIndex];
          if (actionToken) {
            console.log('Action token:', actionToken.type, actionToken.value);

            const withToken = tokens[doIndex+1];
            if (withToken) {
              console.log('Next token:', withToken.type, withToken.value);

              const templateToken = tokens[doIndex+2];
              if (templateToken) {
                console.log('Template token:', templateToken.type, templateToken.value);
              }
            }
          }

          // Parse the action for the ELSE branch
          this.parseAction(tokens, doIndex, elseBranch);

          // Store the parsed action as the fallback
          elseAction = { ...elseBranch.action };
          console.log('Parsed ELSE action:', elseAction);
        }

        // Move current index past this branch
        currentIndex = doIndex;
        continue;
      }

      // Create a branch for IF/ELSEIF
      const branch: Branch = {
        action: { kind: ActionKind.NotifyParticipants } // Default action
      };

      // Skip to next token if it's the end
      if (currentIndex >= tokens.length) {
        branches.push(branch);
        break;
      }

      // Parse the condition for this branch
      const conditionTokens: Token[] = [];
      let parenLevel = 0;
      let foundAction = false;

      // Collect all tokens for this branch's condition
      while (currentIndex < tokens.length) {
        const currToken = tokens[currentIndex];

        if (!currToken) {
          currentIndex++;
          continue;
        }

        // If we encounter the DO keyword, we're at the action part
        if (currToken.type === 'action' && currToken.value === 'DO') {
          foundAction = true;
          currentIndex++;
          break;
        }

        // Track paren level
        if (currToken.type === 'paren') {
          if (currToken.value === '(') parenLevel++;
          else if (currToken.value === ')') parenLevel--;
        }

        // If we hit another branch keyword at paren level 0, we're done with this branch
        if (currToken.type === 'branch' && parenLevel === 0) {
          break;
        }

        conditionTokens.push(currToken);
        currentIndex++;
      }

      // Create the condition node tree from the tokens
      if (conditionTokens.length > 0) {
        branch.conditionNode = this.parseCondition(conditionTokens);
      }

      // Parse action if we found the DO keyword
      if (foundAction) {
        this.parseAction(tokens, currentIndex, branch);
      }

      branches.push(branch);
    }

    return { branches, elseAction };
  }

  /**
   * Parse action part of a rule
   */
  private parseAction(tokens: Token[], startIndex: number, branch: Branch): number {
    let currentIndex = startIndex;
    console.log('parseAction starting at index', startIndex);

    // No tokens left to process
    if (currentIndex >= tokens.length) {
      console.log('No tokens left to process');
      return currentIndex;
    }

    // Get the action type token
    const actionTypeToken = tokens[currentIndex];
    if (!actionTypeToken) {
      console.log('No action type token found');
      return currentIndex;
    }

    console.log('Parsing action token:', actionTypeToken.type, actionTypeToken.value);

    // Process based on action type - case-insensitive comparison
    const actionValue = actionTypeToken.value.toUpperCase();
    if (actionValue === 'SEND_MESSAGE') {
      branch.action.kind = ActionKind.SendMessage;
      console.log('Set action kind to SendMessage');
      currentIndex++;

      // Check for WITH parameter
      if (currentIndex < tokens.length) {
        const withToken = tokens[currentIndex];
        if (withToken && withToken.value.toUpperCase() === 'WITH') {
          console.log('Found WITH token');
          currentIndex++;

          // Get template name
          if (currentIndex < tokens.length) {
            const templateToken = tokens[currentIndex];
            if (templateToken && templateToken.type === 'value') {
              const templateName = this.stripQuotes(templateToken.value);
              branch.action.templateName = templateName;
              console.log('Set template name to:', templateName);
              currentIndex++;
            } else {
              console.log('Missing or invalid template value token');
            }
          }
        }
      }
    } else if (actionValue === 'NOTIFY_PARTICIPANTS') {
      branch.action.kind = ActionKind.NotifyParticipants;
      console.log('Set action kind to NotifyParticipants');
      currentIndex++;
    } else {
      console.log('Unknown action type:', actionTypeToken.value);
    }

    // Skip to next branch or end
    const startSkipIndex = currentIndex;
    while (currentIndex < tokens.length) {
      if (tokens[currentIndex]?.type === 'branch') {
        console.log('Found next branch token at index', currentIndex);
        break;
      }
      currentIndex++;
    }
    console.log(`Skipped ${currentIndex - startSkipIndex} tokens`);

    return currentIndex;
  }

  /**
   * Parse a condition node from tokens
   */
  parseCondition(tokens: Token[]): ConditionNode {
    // Base case - single JSON path with operator
    if (tokens.length === 1 && tokens[0] && tokens[0].type === 'path') {
      return this.createLeafNode(tokens[0].value, ConditionOperator.Exists);
    }

    // Find logical operators (AND, OR)
    const andIndex = this.findOperatorIndex(tokens, 'AND');
    const orIndex = this.findOperatorIndex(tokens, 'OR');

    // If we have a logical operator, split by it
    if (andIndex !== -1 || orIndex !== -1) {
      // Choose the operator with higher precedence (OR has lower precedence than AND)
      const operatorIndex = andIndex !== -1 ? andIndex : orIndex;
      const operator = andIndex !== -1 ? LogicalOperatorDto.And : LogicalOperatorDto.Or;

      // Split tokens by the operator
      const leftTokens = tokens.slice(0, operatorIndex);
      const rightTokens = tokens.slice(operatorIndex + 1);

      // Create compound node with both sides
      return {
        kind: 'COMPOUND',
        operator,
        children: [
          this.parseCondition(leftTokens),
          this.parseCondition(rightTokens)
        ]
      } as CompoundConditionNode;
    }

    // Check for NOT operator - case-insensitive comparison
    if (tokens.length > 1 && tokens[0] && tokens[0].type === 'logic' && tokens[0].value.toUpperCase() === 'NOT') {
      return {
        kind: 'COMPOUND',
        operator: LogicalOperatorDto.Not,
        children: [this.parseCondition(tokens.slice(1))]
      } as CompoundConditionNode;
    }

    // Check for parentheses - keep original case in values
    if (tokens.length > 2 && tokens[0] && tokens[tokens.length - 1] &&
        tokens[0].value === '(' && tokens[tokens.length - 1]?.value === ')') {
      // Remove outer parentheses and parse inner expression
      return this.parseCondition(tokens.slice(1, tokens.length - 1));
    }

    // Check for a leaf condition with a path and operator
    if (tokens.length >= 2) {
      // Find all tokens by type
      const pathTokenIndex = tokens.findIndex(t => t && t.type === 'path');
      const opTokenIndex = tokens.findIndex(t => t && t.type === 'leaf');

      if (pathTokenIndex >= 0 && opTokenIndex >= 0) {
        const pathToken = tokens[pathTokenIndex];
        const opToken = tokens[opTokenIndex];

        if (pathToken && opToken) {
          // Convert string operators to the enum - case-insensitive comparison
          const opMap: Record<string, ConditionOperator> = {
            'EXIST': ConditionOperator.Exists,
            'NOT_EXIST': ConditionOperator.NotExists,
            'EQUALS': ConditionOperator.Equals,
            'CHANGED': ConditionOperator.Changed,
            'CHANGED_FROM_TO': ConditionOperator.ChangedFromTo
          };

          // Use case-insensitive lookup for operator
          const operator = Object.entries(opMap)
            .find(([key]) => key.toUpperCase() === opToken.value.toUpperCase())?.[1]
            || ConditionOperator.Equals;

          // Look for value token after the operator
          let compareValue: string | undefined = undefined;
          let oldValue: string | undefined = undefined;

          // For normal conditions like EQUALS, there should be one value after the operator
          if (operator === ConditionOperator.Equals && opTokenIndex + 1 < tokens.length) {
            const valueToken = tokens[opTokenIndex + 1];
            if (valueToken && valueToken.type === 'value') {
              compareValue = this.stripQuotes(valueToken.value);
            }
          }

          // For CHANGED_FROM_TO, there should be two values
          if (operator === ConditionOperator.ChangedFromTo && opTokenIndex + 2 < tokens.length) {
            const oldValueToken = tokens[opTokenIndex + 1];
            const newValueToken = tokens[opTokenIndex + 2];

            if (oldValueToken && oldValueToken.type === 'value') {
              oldValue = this.stripQuotes(oldValueToken.value);
            }

            if (newValueToken && newValueToken.type === 'value') {
              compareValue = this.stripQuotes(newValueToken.value);
            }
          }

          return this.createLeafNode(
            pathToken.value,
            operator,
            compareValue,
            oldValue
          );
        }
      }
    }

    // Default if nothing else applies
    throw new Error('Invalid condition expression');
  }

  /**
   * Create a leaf condition node
   */
  private createLeafNode(
    path: string,
    operator: ConditionOperator,
    compareValue?: string,
    oldValue?: string
  ): LeafConditionNode {
    const node: LeafConditionNode = {
      kind: 'LEAF',
      targetIdentifier: path,
      operator
    };

    if (compareValue !== undefined) {
      node.compareValue = compareValue;
    }

    if (oldValue !== undefined) {
      node.oldValue = oldValue;
    }

    return node;
  }

  /**
   * Find the index of a logical operator in tokens
   */
  private findOperatorIndex(tokens: Token[], operator: string): number {
    let parenLevel = 0;

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];

      if (!token) continue;

      if (token.type === 'paren') {
        if (token.value === '(') parenLevel++;
        else if (token.value === ')') parenLevel--;
      }

      // Only consider operators at root level (not in parentheses) - case insensitive comparison
      if (parenLevel === 0 && token.type === 'logic' && token.value.toUpperCase() === operator.toUpperCase()) {
        return i;
      }
    }

    return -1;
  }

  /**
   * Tokenize a rule expression
   */
  private tokenize(expression: string): Token[] {
    const tokens: Token[] = [];
    const regex = /(\bIF\b|\bELSEIF\b|\bELSE\b|\bAND\b|\bOR\b|\bNOT\b|\bEXIST\b|\bNOT_EXIST\b|\bEQUALS\b|\bCHANGED\b|\bCHANGED_FROM_TO\b|\bDO\b|\bSEND_MESSAGE\b|\bNOTIFY_PARTICIPANTS\b|\bWITH\b|\$\.\w+(\.\w+)*|"[^"]*"|'[^']*'|\(|\)|\S+)/gi;

    let match;
    while ((match = regex.exec(expression)) !== null) {
      const value = match[0];
      const upperValue = value.toUpperCase();

      // Determine token type
      let type: Token['type'] = 'unknown';

      // Check keyword matches using the enum-like objects
      if (upperValue === BranchKeyword.IF || upperValue === BranchKeyword.ELSEIF || upperValue === BranchKeyword.ELSE) {
        type = 'branch';
      } else if (upperValue === LogicalOperator.AND || upperValue === LogicalOperator.OR || upperValue === LogicalOperator.NOT) {
        type = 'logic';
      } else if (
        upperValue === LeafOperator.EXIST ||
        upperValue === LeafOperator.NOT_EXIST ||
        upperValue === LeafOperator.EQUALS ||
        upperValue === LeafOperator.CHANGED ||
        upperValue === LeafOperator.CHANGED_FROM_TO
      ) {
        type = 'leaf';
      } else if (upperValue === ActionKeyword.DO) {
        type = 'action';
      } else if (upperValue === ActionType.SEND_MESSAGE || upperValue === ActionType.NOTIFY_PARTICIPANTS) {
        type = 'action';
      } else if (upperValue === ActionParam.WITH) {
        type = 'action_param';
      } else if (value.startsWith('$.') || value.startsWith(this.jsonPathPrefix)) {
        type = 'path';
        // For path tokens, preserve the original case by pushing directly and continuing
        tokens.push({ type, value });
        continue;
      } else if ((value.startsWith('"') && value.endsWith('"')) ||
                 (value.startsWith("'") && value.endsWith("'"))) {
        type = 'value';
      } else if (value === '(' || value === ')') {
        type = 'paren';
      }

      // For non-path tokens, store using the matched value
      tokens.push({ type, value });
    }

    return tokens;
  }

  /**
   * Remove quotation marks from a string value
   */
  private stripQuotes(value: string): string {
    if ((value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))) {
      return value.slice(1, -1);
    }
    return value;
  }

  /**
   * Generate a rule expression string from workflow branches
   */
  generateRuleExpression(branches: ConditionalBranch[], fallbackAction?: WorkflowAction): string {
    if (!branches || branches.length === 0) {
      return '';
    }

    const expressionParts: string[] = [];

    // Process each branch using the constants
    branches.forEach((branch, index) => {
      const branchPrefix = index === 0 ? `${BranchKeyword.IF} ` : `${BranchKeyword.ELSEIF} `;
      const conditionPart = this.serializeConditionNode(branch.conditionNode);
      const actionPart = this.serializeAction(branch.action);

      expressionParts.push(`${branchPrefix}${conditionPart} ${ActionKeyword.DO} ${actionPart}`);
    });

    // Add fallback action if provided
    if (fallbackAction) {
      expressionParts.push(`${BranchKeyword.ELSE} ${ActionKeyword.DO} ${this.serializeAction(fallbackAction)}`);
    }

    return expressionParts.join('\n');
  }

  /**
   * Serialize a condition node to a string
   */
  private serializeConditionNode(node?: ConditionNode): string {
    if (!node) {
      return '';
    }

    if (node.kind === 'LEAF') {
      const leafNode = node as LeafConditionNode;
      const path = leafNode.targetIdentifier;

      // Map operator enum to string using the constants
      switch (leafNode.operator) {
        case ConditionOperator.Exists:
          return `${path} ${LeafOperator.EXIST}`;
        case ConditionOperator.NotExists:
          return `${path} ${LeafOperator.NOT_EXIST}`;
        case ConditionOperator.Equals:
          return `${path} ${LeafOperator.EQUALS} '${leafNode.compareValue || ''}'`;
        case ConditionOperator.Changed:
          return `${path} ${LeafOperator.CHANGED}`;
        case ConditionOperator.ChangedFromTo:
          return `${path} ${LeafOperator.CHANGED_FROM_TO} '${leafNode.oldValue || ''}' '${leafNode.compareValue || ''}'`;
        default:
          return path;
      }
    } else {
      const compoundNode = node as CompoundConditionNode;
      let operatorStr: string;

      switch (compoundNode.operator) {
        case LogicalOperatorDto.And:
          operatorStr = LogicalOperator.AND;
          break;
        case LogicalOperatorDto.Or:
          operatorStr = LogicalOperator.OR;
          break;
        case LogicalOperatorDto.Not:
          return `${LogicalOperator.NOT} (${this.serializeConditionNode(compoundNode.children[0])})`;
        default:
          operatorStr = LogicalOperator.AND;
      }

      // For binary operators (AND, OR), join the serialized children with the operator
      if (compoundNode.children.length === 2) {
        return `${this.serializeConditionNode(compoundNode.children[0])} ${operatorStr} ${this.serializeConditionNode(compoundNode.children[1])}`;
      } else if (compoundNode.children.length === 1) {
        return this.serializeConditionNode(compoundNode.children[0]);
      } else {
        // Handle multiple children by chaining operations
        return compoundNode.children
          .map(child => this.serializeConditionNode(child))
          .join(` ${operatorStr} `);
      }
    }
  }

  /**
   * Serialize a workflow action to a string
   */
  private serializeAction(action: WorkflowAction): string {
    if (!action) {
      return ActionType.NOTIFY_PARTICIPANTS;
    }

    if (action.kind === ActionKind.SendMessage) {
      return `${ActionType.SEND_MESSAGE} ${ActionParam.WITH} '${action.templateName || ''}'`;
    } else {
      return ActionType.NOTIFY_PARTICIPANTS;
    }
  }
}

